import { SECONDS_IN_YEAR } from '@context/constants';

type ExchangeRatesType = Record<string, Record<string, number>>;

export const getStakingAPY = (
  farm: IFarmPair,
  rewardRate: number,
  rewardDuration: number,
  totalSupply: number,
  dexRates: DexRatesType,
  exchangeProvider: string,
  exchangeRates?: ExchangeRatesType,
  periodFinished?: number,
): string => {
  const { rewardToken, stakingToken } = farm;

  // Check if period is finished - if so, APY is 0
  if (periodFinished && Date.now() > periodFinished * 1000) {
    // console.warn('[getStakingAPY] Period finished - APY is 0', {
    //   farm: farm.contractAddress,
    //   periodFinished,
    //   periodFinishedDate: new Date(periodFinished * 1000).toISOString(),
    //   currentTime: new Date().toISOString(),
    // });
    return '0.00';
  }

  const totalYearlyRewards = rewardRate * SECONDS_IN_YEAR;

  // Check available rates from both sources
  const rewardTokenExchangeRate =
    exchangeRates?.[rewardToken.abbr?.toUpperCase()]?.USD;
  const rewardTokenDexRate =
    dexRates?.[exchangeProvider]?.[rewardToken.abbr?.toUpperCase()];
  const stakingTokenExchangeRate =
    exchangeRates?.[stakingToken.abbr?.toUpperCase()]?.USD;
  const stakingTokenDexRate =
    dexRates?.[exchangeProvider]?.[stakingToken.abbr?.toUpperCase()];

  // Use exchangeRates (WebSocket) first, fallback to dexRates
  // Validate rates are reasonable (between $0.00001 and $1,000,000)
  // Lowered minimum to accommodate very low-priced tokens like VERSE (~0.000035)
  const MIN_VALID_RATE = 0.00001;
  const MAX_VALID_RATE = 1000000;

  const isValidRate = (rate: number | undefined): boolean => {
    return (
      rate !== undefined &&
      Number.isFinite(rate) &&
      rate > 0 && // Must be positive
      rate >= MIN_VALID_RATE &&
      rate <= MAX_VALID_RATE
    );
  };

  let rewardTokenFiatRate = 0;
  if (isValidRate(rewardTokenExchangeRate)) {
    rewardTokenFiatRate = rewardTokenExchangeRate;
  } else if (isValidRate(rewardTokenDexRate)) {
    rewardTokenFiatRate = rewardTokenDexRate;
  }

  let stakingTokenFiatRate = 0;
  if (isValidRate(stakingTokenExchangeRate)) {
    stakingTokenFiatRate = stakingTokenExchangeRate;
  } else if (isValidRate(stakingTokenDexRate)) {
    stakingTokenFiatRate = stakingTokenDexRate;
  }

  // Warn if rates are missing or invalid
  if (!rewardTokenFiatRate) {
    // console.warn(
    //   `[getStakingAPY] MISSING or INVALID reward token rate for ${rewardToken.abbr}`,
    //   {
    //     exchangeRate: rewardTokenExchangeRate,
    //     dexRate: rewardTokenDexRate,
    //     exchangeRateValid: isValidRate(rewardTokenExchangeRate),
    //     dexRateValid: isValidRate(rewardTokenDexRate),
    //     used: rewardTokenFiatRate,
    //     exchangeRates: exchangeRates?.[rewardToken.abbr?.toUpperCase()],
    //     dexRates:
    //       dexRates?.[exchangeProvider]?.[rewardToken.abbr?.toUpperCase()],
    //   },
    // );
  }
  if (!stakingTokenFiatRate) {
    // console.warn(
    //   `[getStakingAPY] MISSING or INVALID staking token rate for ${stakingToken.abbr}`,
    //   {
    //     exchangeRate: stakingTokenExchangeRate,
    //     dexRate: stakingTokenDexRate,
    //     exchangeRateValid: isValidRate(stakingTokenExchangeRate),
    //     dexRateValid: isValidRate(stakingTokenDexRate),
    //     used: stakingTokenFiatRate,
    //     exchangeRates: exchangeRates?.[stakingToken.abbr?.toUpperCase()],
    //     dexRates:
    //       dexRates?.[exchangeProvider]?.[stakingToken.abbr?.toUpperCase()],
    //   },
    // );
  }

  const yearlyRewardsFiat = totalYearlyRewards * rewardTokenFiatRate;
  const totalSupplyFiat = totalSupply * stakingTokenFiatRate;

  // Guard against invalid calculations
  if (!totalSupplyFiat || totalSupplyFiat <= 0) {
    // console.warn(
    //   '[getStakingAPY] totalSupplyFiat is 0 or invalid - returning "..."',
    //   {
    //     totalSupply,
    //     stakingTokenFiatRate,
    //     totalSupplyFiat,
    //   },
    // );
    return '...';
  }

  if (
    !Number.isFinite(yearlyRewardsFiat) ||
    !Number.isFinite(totalSupplyFiat)
  ) {
    // console.warn('[getStakingAPY] Invalid values detected - returning "..."', {
    //   yearlyRewardsFiat,
    //   totalSupplyFiat,
    //   totalYearlyRewards,
    //   rewardTokenFiatRate,
    // });
    return '...';
  }

  const estimatedAPY = (yearlyRewardsFiat / totalSupplyFiat) * 100;

  // Guard against NaN or Infinity
  if (!Number.isFinite(estimatedAPY)) {
    // console.warn(
    //   '[getStakingAPY] estimatedAPY is NaN or Infinity - returning "..."',
    //   {
    //     yearlyRewardsFiat,
    //     totalSupplyFiat,
    //     estimatedAPY,
    //   },
    // );
    return '...';
  }

  if (estimatedAPY > 500) return '> 500';
  return estimatedAPY.toFixed(2);
};
